const FAVORITES_KEY = 'attain.favorites.v1';
export const FAVORITES_UPDATED_EVENT = 'favorites-updated';

type FavoriteSlug = string;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function dispatchFavoritesUpdate(slugs: FavoriteSlug[]) {
  if (!isBrowser()) return;
  try {
    window.dispatchEvent(
      new CustomEvent(FAVORITES_UPDATED_EVENT, {
        detail: { slugs },
      }),
    );
  } catch {
    // no-op
  }
}

export function getFavoriteSlugs(): FavoriteSlug[] {
  if (!isBrowser()) return [];
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

export function setFavoriteSlugs(slugs: FavoriteSlug[]): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(slugs));
    dispatchFavoritesUpdate(slugs);
  } catch {
    // swallow
  }
}

export function addFavorite(slug: FavoriteSlug): FavoriteSlug[] {
  const current = getFavoriteSlugs();
  if (!current.includes(slug)) {
    current.push(slug);
    setFavoriteSlugs(current);
  }
  return current;
}

export function removeFavorite(slug: FavoriteSlug): FavoriteSlug[] {
  const current = getFavoriteSlugs();
  const next = current.filter((s) => s !== slug);
  setFavoriteSlugs(next);
  return next;
}

export function isFavorited(slug: FavoriteSlug): boolean {
  return getFavoriteSlugs().includes(slug);
}

export type { FavoriteSlug };
// localStorage keys used by favorites system:
// - ns_favorites: array of favorite domain objects

interface FavoriteDomain {
  domain: string;
  savedAt: number;
}

/**
 * Get all favorite domains from localStorage
 */
export function getFavorites(): FavoriteDomain[] {
  try {
    const stored = localStorage.getItem('ns_favorites');
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];

    return parsed.sort((a, b) => b.savedAt - a.savedAt); // Most recent first
  } catch (error) {
    console.debug('Error loading favorites from localStorage:', error);
    return [];
  }
}

/**
 * Check if a domain is favorited
 */
export function isFavorite(domain: string): boolean {
  const favorites = getFavorites();
  return favorites.some((fav) => fav.domain === domain);
}

/**
 * Toggle favorite status of a domain
 */
export function toggleFavorite(domain: string): boolean {
  const favorites = getFavorites();
  const existingIndex = favorites.findIndex((fav) => fav.domain === domain);

  let newFavorites: FavoriteDomain[];
  let isNowFavorite: boolean;

  if (existingIndex >= 0) {
    // Remove from favorites
    newFavorites = favorites.filter((fav) => fav.domain !== domain);
    isNowFavorite = false;
  } else {
    // Add to favorites
    const newFav: FavoriteDomain = {
      domain,
      savedAt: Date.now(),
    };
    newFavorites = [newFav, ...favorites].slice(0, 200); // Cap at 200
    isNowFavorite = true;
  }

  try {
    localStorage.setItem('ns_favorites', JSON.stringify(newFavorites));
    // Dispatch custom event to notify components
    window.dispatchEvent(new CustomEvent('favorites-updated'));
  } catch (error) {
    console.debug('Error saving favorites to localStorage:', error);
  }

  return isNowFavorite;
}

/**
 * Clear all favorites
 */
export function clearFavorites(): number {
  const prevCount = getFavorites().length;

  try {
    localStorage.removeItem('ns_favorites');
    // Dispatch custom event to notify components
    window.dispatchEvent(new CustomEvent('favorites-updated'));
  } catch (error) {
    console.debug('Error clearing favorites:', error);
  }

  return prevCount;
}

/**
 * Get favorite domains count
 */
export function getFavoritesCount(): number {
  return getFavorites().length;
}
